import * as React from 'react';
import { useField } from 'formik';
import TextField, { TextFieldProps } from '@material-ui/core/TextField';
import InputAdornment from '@material-ui/core/InputAdornment';
import IconButton from '@material-ui/core/IconButton';
import Visibility from '@material-ui/icons/Visibility';
import VisibilityOff from '@material-ui/icons/VisibilityOff';
import { hasValue } from './inputHelpers';

interface IProps {
  name: string;
  isHidden?: boolean;
  isPassword?: boolean;
  /** Optional icon shown at the start of the field. */
  startIcon?: React.ReactNode;
  /** Keep the label floating above the field (e.g. when a prefix is shown). */
  labelShrunk?: boolean;
}

const XTextInput = ({
  name,
  isHidden = false,
  isPassword = false,
  startIcon,
  labelShrunk = false,
  margin = 'normal',
  ...props
}: TextFieldProps & IProps) => {
  const [field, meta, helpers] = useField({ name });

  const [value, setValue] = React.useState(field.value || ''); // Store the value in state
  const inputRef = React.useRef<HTMLInputElement | null>(null);
  const [focused, setFocused] = React.useState(false);
  const [autofilled, setAutofilled] = React.useState(false);
  const fieldValueRef = React.useRef(field.value);
  fieldValueRef.current = field.value;

  // Browsers fill saved credentials without firing a React change event, so
  // the label would sit on top of the filled-in text and Formik would still
  // see an empty value. Detect it, float the label and sync the value.
  React.useEffect(() => {
    const el = inputRef.current;
    if (!el) return undefined;
    const check = () => {
      let filled = Boolean(el.value);
      try {
        filled = filled || el.matches(':-webkit-autofill');
      } catch {
        /* selector unsupported — fall back to the value check */
      }
      setAutofilled(filled);
      if (el.value && el.value !== fieldValueRef.current) {
        helpers.setValue(el.value);
      }
    };
    check();
    const timers = [setTimeout(check, 150), setTimeout(check, 800)];
    el.addEventListener('animationstart', check);
    return () => {
      timers.forEach(clearTimeout);
      el.removeEventListener('animationstart', check);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [showPassword, setShowPassword] = React.useState(false); // hide and show password

  React.useEffect(() => {
    if (field.value !== value) {
      setValue(field.value || '');
    }
  }, [field.value]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value;
    setValue(newValue);
    helpers.setValue(newValue); // Update Formik's field value
  };

  const error = hasValue(meta.error) ? meta.error : undefined;
  const showError = Boolean(error && meta.touched);
  if (isHidden) {
    return (
      <input
        type="hidden"
        name={field.name}
        value={field.value}
        onChange={field.onChange}
      />
    );
  }
  return (
    <TextField
      {...field}
      {...props}
      type={isPassword && !showPassword ? 'password' : 'text'}
      margin={margin}
      fullWidth
      error={showError}
      helperText={showError && error}
      value={value}
      onChange={handleChange}
      inputRef={inputRef}
      onFocus={(e) => {
        setFocused(true);
        props.onFocus?.(e);
      }}
      onBlur={(e) => {
        setFocused(false);
        field.onBlur(e);
        props.onBlur?.(e);
      }}
      InputLabelProps={{
        ...props.InputLabelProps,
        // MUI floats the label whenever a start icon is present; with an icon
        // it should rest beside it and only float on focus or a value.
        ...(startIcon
          ? { shrink: labelShrunk || focused || Boolean(value) || autofilled }
          : {}),
        className: startIcon
          ? `ev-label-adorned ${props.InputLabelProps?.className ?? ''}`.trim()
          : props.InputLabelProps?.className,
      }}
      autoComplete="nope"
      InputProps={{
        startAdornment: startIcon ? (
          <InputAdornment position="start">{startIcon}</InputAdornment>
        ) : undefined,
        endAdornment: isPassword ? (
          <InputAdornment position="end">
            <IconButton
              aria-label={showPassword ? 'Hide password' : 'Show password'}
              onClick={() => setShowPassword((show) => !show)}
              onMouseDown={(e) => e.preventDefault()}
              edge="end"
            >
              {showPassword ? <VisibilityOff /> : <Visibility />}
            </IconButton>
          </InputAdornment>
        ) : undefined,
      }}
    />
  );
};

export default XTextInput;
